import useMediaQuery from "../../../../hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import Skeleton from "../../../ui/skeleton";
import {
  arrowDownBlack,
  arrowUpBlack,
  openMenu,
  closeMenu,
} from "../../../../assets/svgs";
import { Language } from "../language/language";
import { isEmptyArray, isRouteActive, overflowCheck } from "../../../../utils/checkers";
import { useTanstack } from "../../../../hooks/useTanstack";
import useOutsideClick from "../../../../hooks/useOutsideClick";
import { smoothScroll } from "../../../../utils/helpers";
import styles from "./routes.module.scss";

export const Routes = () => {
  const dropdownRef = useRef(null);
  const navRef = useRef(null);

  const mobileFirst = useMediaQuery(`( max-width: 1123px )`);

  const { t } = useTranslation();
  const { language } = useParams();
  const { pathname } = useLocation();
  const { data } = useTanstack("getWebItems");

  const [menu, setMenu] = useState(false);
  const [hoveredDropdownId, setHoveredDropdownId] = useState(null);

  const getActiveDropdownId = () => {
    if (mobileFirst && menu) {
      if (!sortedHeaderItems) return null;

      for (const item of sortedHeaderItems) {
        if (item.dropdown && !isEmptyArray(item.dropdown)) {
          const hasActiveDropdownItem = item.dropdown.some((dropdownItem) => {
            const normalizedPathname = pathname.startsWith("/")
              ? pathname
              : `/${pathname}`;
            const normalizedDropdownPath = dropdownItem.path.startsWith("/")
              ? dropdownItem.path
              : `/${dropdownItem.path}`;
            return normalizedPathname === normalizedDropdownPath;
          });
          if (hasActiveDropdownItem) {
            return item.id;
          }
        }
      }
    }

    return hoveredDropdownId;
  };

  const sortedHeaderItems =
    data?.sort((a, b) => (a.id === 3 ? -1 : b.id === 3 ? 1 : 0)) || [];

  const handleDropdownClick = (e, id) => {
    e.preventDefault();
    if (mobileFirst) {
      setHoveredDropdownId(hoveredDropdownId === id ? null : id);
    }
  };

  const handlePageSelect = () => {
    menu && setMenu(false);
    setHoveredDropdownId(null);
  };

  const handleMouseEnter = (id) => {
    if (!mobileFirst) {
      setHoveredDropdownId(id);
    }
  };

  const handleMouseLeave = () => {
    if (!mobileFirst) {
      setHoveredDropdownId(null);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (menu && !mobileFirst) {
        setMenu(false);
      }
      if (!mobileFirst) {
        setHoveredDropdownId(null);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [menu, mobileFirst]);

  useOutsideClick(navRef, menu, setMenu);
  overflowCheck(mobileFirst && menu);

  if (!data) {
    return <Skeleton type="routes" number={1} />;
  }

  return (
    <div className={styles.routes}>
      {mobileFirst && (
        <div className={styles.mobile_routes}>
          {!menu && (
            <button
              onClick={() => setMenu(!menu)}
              type="button"
              aria-label="open"
            >
              {openMenu.icon}
              <span>{t("menu")}</span>
            </button>
          )}
          {menu && (
            <button
              onClick={() => setMenu(!menu)}
              type="button"
              aria-label="close"
            >
              {closeMenu.icon}
              <span>{t("menu")}</span>
            </button>
          )}
        </div>
      )}

      {(mobileFirst && menu) || !mobileFirst ? (
        <nav className={styles.routes_nav} ref={navRef}>
          <ul className={styles.routes_list}>
            <li key="home">
              <NavLink
                to={`/${language}`}
                onClick={() => (setMenu(false), smoothScroll())}
                className={`${
                  pathname === `/${language}`
                    ? styles.routes_link_selected
                    : styles.routes_link_route
                }`}
              >
                {t("home")}
              </NavLink>
            </li>
            {sortedHeaderItems?.map(({ id, path, name, dropdown }) => {
              return isEmptyArray(dropdown) ? (
                <li className={styles.routes_link} key={id}>
                  <NavLink
                    to={path}
                    onClick={() => setMenu(false)}
                    className={({ isActive }) =>
                      `${styles.routes_link_route} ${
                        isActive && styles.routes_link_selected
                      }`
                    }
                  >
                    {name}
                  </NavLink>
                </li>
              ) : (
                <li
                  key={id}
                  className={styles.routes_link}
                  ref={dropdownRef}
                  onMouseEnter={() => handleMouseEnter(id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    aria-label={`dropdown${id}`}
                    onClick={(e) => handleDropdownClick(e, id)}
                    className={`${
                      isRouteActive({ id, dropdown, path }, pathname, language)
                        ? styles.routes_link_selected
                        : styles.routes_link_route
                    }`}
                  >
                    {t(name)}
                    {getActiveDropdownId() !== id
                      ? arrowDownBlack.icon
                      : arrowUpBlack.icon}
                  </button>
                  <div
                    className={`${styles.routes_drop} ${
                      getActiveDropdownId() === id ? styles.active : ""
                    }`}
                  >
                    {dropdown && (
                      <ul className={styles.routes_maindrop}>
                        {dropdown?.map(({ id, name, path }) => {
                          return (
                            <li
                              ref={dropdownRef}
                              key={id}
                              onClick={handlePageSelect}
                            >
                              <NavLink
                                to={path}
                                className={({ isActive }) =>
                                  `${styles.routes_link} ${
                                    isActive && styles.active
                                  }`
                                }
                              >
                                {name}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
            {/* <li>
              <NavLink
                to="/contact"
                onClick={() => dispatch(setMenu(false))}
                className={`${
                  pathname === "/contact"
                    ? styles.routes_link_selected
                    : styles.routes_link_route
                }`}
              >
                {t("contact")}
              </NavLink>
            </li> */}
          </ul>
        </nav>
      ) : null}
      <Language />
    </div>
  );
};
